import * as ReactGA from 'react-ga';
import TagManager from 'react-gtm-module';
import { AnalyticsSettings, AppSettings } from '../settings/appSettings';
import dayjs from 'dayjs';
import { IEventStoreState, ISeatStoreState } from '../store/event/types';
import { IBookTicketQuotaStoreState } from '../store/ticket/types';

type GtmEcommerceItem = {
  item_id: string;
  item_name: string;
  affiliation: string;
  discount: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_variant: string;
  price: number;
  item_id_additional: string;
  quantity: number;
};

type GtmEcommercePayload = {
  currency: string;
  value?: number;
  coupon?: string;
  items: GtmEcommerceItem[];
  transaction_id?: string;
  tax?: number;
  shipping?: number;
  payment_type?: string;
  checkout_type?: string;
  customer_id?: string;
};

export class AnalyticService {
   private initializedPixelIds: Set<string> = new Set();
  private readonly brand = 'Fanaticka';

  trackEvent(data: string, value?: any) {
    if (!AppSettings.IS_DEV) {
      ReactGA.event({
        category: 'main',
        action: data,
        label: 'no',
      });

      const ReactPixel = require('react-facebook-pixel').default;
      ReactPixel.track(data, value);
    }
  }

  initializePixel(pixelId: string) {
    if (AppSettings.IS_DEV || !pixelId || this.initializedPixelIds.has(pixelId)) {
      return;
    }

    try {
      const ReactPixel = require('react-facebook-pixel').default;
      ReactPixel.init(pixelId, {}, { debug: false, autoConfig: true });
      ReactPixel.pageView();
      this.initializedPixelIds.add(pixelId);
    } catch (error) {
      console.error('Failed to initialize Facebook Pixel:', error);
    }
  }

  trackEventWithPixel(eventName: string, value?: any, pixelId?: string) {
    if (AppSettings.IS_DEV) {
      return;
    }

    try {
      const ReactPixel = require('react-facebook-pixel').default;
      
      // Если указан конкретный Pixel ID, инициализируем его если нужно
      if (pixelId && !this.initializedPixelIds.has(pixelId)) {
        this.initializePixel(pixelId);
      }
      
      // Отслеживаем событие
      ReactPixel.track(eventName, value);
    } catch (error) {
      console.error('Failed to track Facebook Pixel event:', error);
    }
  }

  trackPageView(data: string) {
    if (!AppSettings.IS_DEV) {
      ReactGA.pageview(data);
    }
  }

  initialize() {
    if (!AppSettings.IS_DEV) {
      ReactGA.initialize(AnalyticsSettings.GOOGLE_TRACKING_ID);
      const ReactPixel = require('react-facebook-pixel').default;
      ReactPixel.init('528267061237134', {}, { debug: false, autoConfig: true });
      ReactPixel.pageView();
      this.initializedPixelIds.add('528267061237134');
    }
    TagManager.initialize({ gtmId: 'GTM-PC5SSP6' });
  }

  trackAddToCart(params: { event: IEventStoreState; seat: ISeatStoreState }) {
    const item = this.buildItemFromSeat(params.event, params.seat);
    if (!item) return;
    this.pushEcommerceEvent('add_to_cart', {
      currency: this.getCurrencyFromSeat(params.event, params.seat) || 'TRY',
      value: item.price,
      coupon: '',
      items: [item],
    });
  }

  trackRemoveFromCart(params: { event: IEventStoreState; seat: ISeatStoreState }) {
    const item = this.buildItemFromSeat(params.event, params.seat);
    if (!item) return;
    this.pushEcommerceEvent('remove_from_cart', {
      currency: this.getCurrencyFromSeat(params.event, params.seat) || 'TRY',
      value: item.price,
      coupon: '',
      items: [item],
    });
  }

  trackBeginCheckout(params: { event: IEventStoreState; seats: ISeatStoreState[] }) {
    const items = (params.seats || []).map((s) => this.buildItemFromSeat(params.event, s)).filter(Boolean) as GtmEcommerceItem[];
    if (items.length === 0) return;
    const currency = this.getCurrencyFromSeat(params.event, params.seats?.[0]) || 'TRY';
    const value = items.reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
    this.pushEcommerceEvent('begin_checkout', { currency, value, coupon: '', items });
  }

  trackAddPaymentInfo(params: { event: IEventStoreState; seats: ISeatStoreState[] }) {
    const payload = this.buildCheckoutItemsPayload(params.event, params.seats);
    if (!payload) return;
    this.pushEcommerceEvent('add_payment_info', payload);
  }

  /** Logged-in checkout step with points / payment method choice (same ecommerce shape as add_payment_info). */
  trackReviewPointsInfo(params: { event: IEventStoreState; seats: ISeatStoreState[] }) {
    const payload = this.buildCheckoutItemsPayload(params.event, params.seats);
    if (!payload) return;
    this.pushEcommerceEvent('review_points_info', payload);
  }

  private buildCheckoutItemsPayload(
    event: IEventStoreState,
    seats: ISeatStoreState[],
  ): Omit<GtmEcommercePayload, 'transaction_id' | 'tax' | 'shipping' | 'payment_type' | 'checkout_type' | 'customer_id'> | null {
    const items = (seats || []).map((s) => this.buildItemFromSeat(event, s)).filter(Boolean) as GtmEcommerceItem[];
    if (items.length === 0) return null;
    const currency = this.getCurrencyFromSeat(event, seats?.[0]) || 'TRY';
    const value = items.reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
    return { currency, value, coupon: '', items };
  }

  trackPurchase(params: {
    event: IEventStoreState;
    seats: ISeatStoreState[];
    transactionId: string;
    value: number;
    currency: string;
    paymentType?: string;
    checkoutType?: string;
    customerId?: string;
  }) {
    const items = (params.seats || []).map((s) => this.buildItemFromSeat(params.event, s)).filter(Boolean) as GtmEcommerceItem[];
    if (items.length === 0) return;
    this.pushEcommerceEvent('purchase', {
      currency: params.currency || this.getCurrencyFromSeat(params.event, params.seats?.[0]) || 'TRY',
      transaction_id: params.transactionId,
      value: Number(params.value) || 0,
      coupon: '',
      tax: 0,
      shipping: 0,
      payment_type: params.paymentType || 'Credit card',
      checkout_type: params.checkoutType || '',
      customer_id: params.customerId || '',
      items,
    });
  }

  private pushEcommerceEvent(eventName: string, ecommerce: GtmEcommercePayload) {
    try {
      if (typeof window === 'undefined') return;
      const dl = (window as any).dataLayer || ((window as any).dataLayer = []);
      dl.push({ ecommerce: null });
      dl.push({ event: eventName, ecommerce });
    } catch (e) {
      // analytics must never break UX
      if (AppSettings.IS_DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to push GTM ecommerce event', eventName, e);
      }
    }
  }

  private buildItemFromSeat(event: IEventStoreState, seat: ISeatStoreState): GtmEcommerceItem | null {
    if (!event || !seat) return null;
    const quota = this.findQuota(event, seat.quotaId);
    const sector = event.sectors?.find((s) => s.slug === seat.sectorSlug);
    const sectorTitle = seat.sectorTitle || sector?.title || seat.sectorSlug || '';
    const concertCategory = this.buildConcertCategory(event);
    const itemId = this.buildItemId(event, seat, quota);
    const price = Number(quota?.price ?? 0);

    const variantParts: string[] = [];
    if (sectorTitle) variantParts.push(`Sec ${sectorTitle}`);
    if (seat.rowSlug) variantParts.push(`row ${seat.rowSlug}`);
    if (seat.seatSlug) variantParts.push(`seat ${seat.seatSlug}`);
    const itemVariant = variantParts.join(', ');

    return {
      item_id: itemId,
      item_name: this.buildItemName(event, sectorTitle),
      affiliation: this.brand,
      discount: 0,
      item_brand: this.brand,
      item_category: concertCategory,
      item_category2: sectorTitle,
      item_variant: itemVariant,
      price,
      item_id_additional: '',
      quantity: 1,
    };
  }

  private buildConcertCategory(event: IEventStoreState) {
    const cityOrVenue = event?.venue?.city || event?.venue?.title || '';
    const date = (event?.date && dayjs.isDayjs(event.date) ? event.date : dayjs(event?.date as any)).format('YYYY.MM.DD');
    return [cityOrVenue, date].filter(Boolean).join(' - ');
  }

  private buildItemName(event: IEventStoreState, sectorTitle: string) {
    const category = this.buildConcertCategory(event);
    return [category, sectorTitle].filter(Boolean).join(' - ');
  }

  private buildItemId(event: IEventStoreState, seat: ISeatStoreState, quota?: IBookTicketQuotaStoreState) {
    const base = quota?.id || seat.quotaId;
    const prefix = event?.slug ? `${event.slug}:` : '';
    return `${prefix}${base}`;
  }

  private findQuota(event: IEventStoreState, quotaId: number): IBookTicketQuotaStoreState | undefined {
    const sectors = event?.sectors || [];
    for (const s of sectors) {
      const q = s?.ticketQuotas?.find((x) => x.id === quotaId);
      if (q) return q;
    }
    return undefined;
  }

  private getCurrencyFromSeat(event: IEventStoreState, seat: ISeatStoreState) {
    if (!event || !seat) return undefined;
    const quota = this.findQuota(event, seat.quotaId);
    return quota?.currency;
  }
}
